import { useEffect, useMemo, useRef } from 'react'
import { SimpleGrid, Stack, TextInput } from '@mantine/core'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { SaveFooter } from '@/components/ui/SaveFooter'
import { USPhoneInput } from '@/components/ui/USPhoneInput'
import { useUpdateMyProfile, type MyProfile } from '@/hooks/useMyProfile'

// What the US-formatted input produces: `+1` and ten digits.
const US_MOBILE_PHONE = /^\+1\d{10}$/

// The saved phone passes as it is, whatever its format: a number stored before the console
// wrote E.164 is left alone until the member edits it (and then it is not re-sent either).
const personalSchema = (savedPhone: () => string) =>
  z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    mobilePhone: z
      .string()
      .refine(
        (value) => value === '' || value === savedPhone() || US_MOBILE_PHONE.test(value),
        'Enter a 10-digit US phone number',
      ),
  })
type PersonalFormValues = z.infer<ReturnType<typeof personalSchema>>

const toValues = (profile: MyProfile): PersonalFormValues => ({
  firstName: profile.firstName,
  lastName: profile.lastName,
  mobilePhone: profile.mobilePhone ?? '',
})

/**
 * Personal information: the member's legal names and mobile phone, each its own field with
 * one Save for the tab; the work email is shown read-only (an administrator's to change).
 * Login codes go to the mobile phone, so an edited number says so before it is saved.
 */
export function PersonalInformationTab({
  profile,
  onDirtyChange,
}: {
  profile: MyProfile
  /** Tells the page whether the tab holds unsaved edits (the tab dot, the reload warning). */
  onDirtyChange?: (dirty: boolean) => void
}) {
  // The phone the form started from. It moves with the form's resets, not with every
  // background refetch: a name-only save must not re-send a number another device changed
  // meanwhile as if the member had edited it.
  const savedPhone = useRef(profile.mobilePhone ?? '')
  const resolver = useMemo(() => zodResolver(personalSchema(() => savedPhone.current)), [])
  const form = useForm<PersonalFormValues>({ resolver, defaultValues: toValues(profile) })
  const update = useUpdateMyProfile()
  const { isDirty } = form.formState

  // Re-populated after a refetch only while the form holds no edits: react-query refetches
  // in the background, and a reset then would drop what the member is typing.
  useEffect(() => {
    if (!isDirty) {
      savedPhone.current = profile.mobilePhone ?? ''
      form.reset(toValues(profile))
    }
  }, [profile, isDirty, form])
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const save = async (values: PersonalFormValues) => {
    try {
      await update.mutateAsync({
        firstName: values.firstName,
        lastName: values.lastName,
        // Only an edited phone is sent; blank clears it.
        ...(values.mobilePhone === savedPhone.current
          ? {}
          : { mobilePhone: values.mobilePhone || null }),
      })
      // The refetch has landed (the hook awaits it); what was sent is what is now stored.
      savedPhone.current = values.mobilePhone
      form.reset(values)
    } catch {
      // The hook raised the toast and the footer shows it; the edits stay for a retry.
    }
  }

  const cancel = () => {
    form.reset()
    update.reset()
  }

  return (
    <form onSubmit={form.handleSubmit(save)} noValidate>
      <fieldset disabled={update.isPending} className="m-0 min-w-0 border-0 p-0">
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            <Controller
              name="firstName"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextInput {...field} label="First name" error={fieldState.error?.message} />
              )}
            />
            <Controller
              name="lastName"
              control={form.control}
              render={({ field, fieldState }) => (
                <TextInput {...field} label="Last name" error={fieldState.error?.message} />
              )}
            />
          </SimpleGrid>
          <TextInput
            label="Work email"
            description="Contact an administrator to change your work email"
            value={profile.email ?? ''}
            readOnly
          />
          <Controller
            name="mobilePhone"
            control={form.control}
            render={({ field, fieldState }) => (
              <USPhoneInput
                {...field}
                label="Mobile phone"
                placeholder="(555) 555-5555"
                description={
                  fieldState.isDirty ? 'Login codes will go to this number from now on' : undefined
                }
                error={fieldState.error?.message}
              />
            )}
          />
        </Stack>
      </fieldset>
      <SaveFooter
        saveError={
          update.error ? { title: 'Profile not saved', message: update.error.message } : null
        }
        isSaving={update.isPending}
        isDirty={isDirty}
        onCancel={cancel}
      />
    </form>
  )
}
